import { useCaseChat } from "@/hooks/useCaseChat";
import { useCallback, useEffect, useRef, useState } from "react";

import { CaseChatPanelView } from "./CaseChatPanelView";
import { DEFAULT_CHAT_PANEL_WIDTH, MAX_CHAT_PANEL_WIDTH, MIN_CHAT_PANEL_WIDTH } from "./constants";

export interface CaseChatPanelProps {
  isOpen: boolean;
  onClose: () => void;
  caseId: string;
  activeConversationId?: string | null;
  onNewConversation?: () => void;
  resetToken?: number;
  topOffset?: number;
  rightOffset?: number | string;
  initialWidth?: number;
  minWidth?: number;
  maxWidth?: number;
  onWidthChange?: (width: number) => void;
}

export const CaseChatPanel: React.FC<CaseChatPanelProps> = ({
  isOpen,
  onClose,
  caseId,
  activeConversationId = null,
  onNewConversation,
  resetToken,
  topOffset = 64,
  rightOffset = 0,
  initialWidth = DEFAULT_CHAT_PANEL_WIDTH,
  minWidth = MIN_CHAT_PANEL_WIDTH,
  maxWidth = MAX_CHAT_PANEL_WIDTH,
  onWidthChange,
}) => {
  const {
    messages,
    title,
    sendMessage,
    isStreaming,
    isLoadingHistory,
    error,
    startNewConversation,
  } = useCaseChat(caseId, activeConversationId, resetToken);
  const [panelWidth, setPanelWidth] = useState(initialWidth);
  const [question, setQuestion] = useState("");
  const panelRef = useRef<HTMLDivElement | null>(null);
  const widthRef = useRef(initialWidth);
  const scrollAreaRef = useRef<HTMLDivElement | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const isPinnedToBottomRef = useRef(true);

  const getViewport = useCallback(
    () => scrollAreaRef.current?.querySelector<HTMLDivElement>("[data-radix-scroll-area-viewport]"),
    []
  );

  useEffect(() => {
    const viewport = getViewport();
    if (!viewport) return undefined;

    const handleScroll = () => {
      const distanceFromBottom = viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight;
      isPinnedToBottomRef.current = distanceFromBottom < 48;
    };
    viewport.addEventListener("scroll", handleScroll, { passive: true });
    return () => viewport.removeEventListener("scroll", handleScroll);
  }, [getViewport]);

  const hasMessages = messages.length > 0;

  // A ResizeObserver, not a `messages`-keyed effect: useTypewriterReveal keeps
  // growing message content well after `messages` itself last changed.
  useEffect(() => {
    if (!hasMessages) return undefined;
    const viewport = getViewport();
    const messagesEl = messagesContainerRef.current;
    if (!viewport || !messagesEl) return undefined;

    const resizeObserver = new ResizeObserver(() => {
      if (isPinnedToBottomRef.current) {
        viewport.scrollTop = viewport.scrollHeight;
      }
    });
    resizeObserver.observe(messagesEl);
    return () => resizeObserver.disconnect();
  }, [hasMessages, getViewport]);

  const handleResizeStart = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      const handle = e.currentTarget;
      const startX = e.clientX;
      const startWidth = widthRef.current;

      handle.setPointerCapture(e.pointerId);
      document.body.style.userSelect = "none";
      document.body.style.cursor = "col-resize";

      const onMove = (moveEvent: PointerEvent) => {
        const delta = startX - moveEvent.clientX;
        const newWidth = Math.max(minWidth, Math.min(maxWidth, startWidth + delta));
        widthRef.current = newWidth;
        if (panelRef.current) {
          panelRef.current.style.width = `${newWidth}px`;
        }
        document.documentElement.style.setProperty("--chat-panel-width", `${newWidth}px`);
      };

      const endResize = () => {
        handle.removeEventListener("pointermove", onMove);
        handle.removeEventListener("pointerup", endResize);
        handle.removeEventListener("pointercancel", endResize);
        document.body.style.userSelect = "";
        document.body.style.cursor = "";
        setPanelWidth(widthRef.current);
        onWidthChange?.(widthRef.current);
      };

      handle.addEventListener("pointermove", onMove);
      handle.addEventListener("pointerup", endResize);
      handle.addEventListener("pointercancel", endResize);
    },
    [minWidth, maxWidth, onWidthChange]
  );

  useEffect(
    () => () => {
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    },
    []
  );

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (!question.trim() || isStreaming) return;
      isPinnedToBottomRef.current = true;
      void sendMessage(question);
      setQuestion("");
    },
    [question, isStreaming, sendMessage]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSubmit(e);
      }
    },
    [handleSubmit]
  );

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [question]);

  const handleNewConversation = useCallback(() => {
    startNewConversation();
    onNewConversation?.();
  }, [startNewConversation, onNewConversation]);

  if (!isOpen) return null;

  const containerStyle: React.CSSProperties = {
    position: "fixed",
    right: rightOffset,
    top: `${topOffset}px`,
    height: `calc(100vh - ${topOffset}px)`,
    width: `${panelWidth}px`,
    zIndex: "var(--z-drawer)",
  };

  return (
    <CaseChatPanelView
      containerStyle={containerStyle}
      panelRef={panelRef}
      scrollAreaRef={scrollAreaRef}
      messagesContainerRef={messagesContainerRef}
      textareaRef={textareaRef}
      title={title}
      onClose={onClose}
      onResizeStart={handleResizeStart}
      onNewConversation={handleNewConversation}
      isNewConversationDisabled={messages.length === 0 || isStreaming}
      messages={messages}
      isStreaming={isStreaming}
      isLoadingHistory={isLoadingHistory}
      error={error}
      question={question}
      onQuestionChange={setQuestion}
      onKeyDown={handleKeyDown}
      onSubmit={handleSubmit}
    />
  );
};
